import { t } from 'i18next';

import {
  type FinancialStatementRawItem,
  getAssetOverviewFinancialsUrl,
  getStatementDisplayLabel,
  normalizeFinancialStatementItems,
} from '../financial-statements-item/financialStatementUtils';
import type {
  ConversationInventory,
  FormattedConversationInventory,
} from '../services/inventory-service';
import { htmlNodes } from './htmlNodes';
import {
  type KgExportGroup,
  kgExportGroupsToHtml,
  kgExportGroupsToPlainText,
  resolveKgForSourcesSection,
} from './kgSourcesForCopy';

type SourcesSection = {
  title: string;
  textItems: string[];
  htmlItems: string[];
  kgExportGroups?: KgExportGroup[];
};

export type InventoryContent = { html: string; text: string };

export interface BuildInventoryContentParams {
  inventory?: ConversationInventory | FormattedConversationInventory;
  baseUrl?: string;
  rawFinancialStatementItems?: FinancialStatementRawItem[];
}

export const buildInventoryContent = async ({
  inventory,
  baseUrl,
  rawFinancialStatementItems,
}: BuildInventoryContentParams = {}): Promise<InventoryContent> => {
  if (!inventory || Object.keys(inventory).length === 0) {
    return { html: '', text: '' };
  }

  const { kgExportGroups } = await resolveKgForSourcesSection(inventory);

  const insightTitles =
    inventory && 'insightTitles' in inventory
      ? inventory.insightTitles
      : undefined;
  let html = '';
  let text = '';

  const entities = inventory?.entity?.items?.map(i => i.tag) ?? [];
  const timeseries = inventory?.timeseries?.items?.map(i => i.snake) ?? [];
  const webLinks =
    inventory?.['web-links']?.items?.flatMap(i => i.urls ?? []) ?? [];
  const financialStatementItems = normalizeFinancialStatementItems(
    inventory?.['financial-statement']?.items ?? []
  );
  const rawItemsForLinks = normalizeFinancialStatementItems(
    rawFinancialStatementItems ??
      inventory?.['financial-statement']?.items ??
      []
  );
  const financialStatementsText = financialStatementItems.map(item =>
    item.statement
      ? `${item.entityTag} - ${getStatementDisplayLabel(item.statement)}`
      : item.entityTag
  );
  const financialStatementsHtml = baseUrl
    ? financialStatementItems.map((item, i) => {
        const rawItem = rawItemsForLinks[i];
        const entityTag = rawItem?.entityTag ?? item.entityTag;
        const displayText = item.statement
          ? `${item.entityTag} - ${getStatementDisplayLabel(item.statement)}`
          : item.entityTag;
        return htmlNodes.a(
          displayText,
          getAssetOverviewFinancialsUrl(entityTag, baseUrl, item.statement)
        );
      })
    : financialStatementsText;
  const externalData =
    inventory?.['external-data']?.items?.flatMap(i => i.links ?? []) ?? [];

  const newsHeadlines = inventory?.news?.items?.map(item => item.title) ?? [];

  const insightHeadlines =
    inventory?.insight?.items?.map(
      item => insightTitles?.[item.id] ?? item.id
    ) ?? [];

  const watchlistNames =
    inventory?.watchlist?.items?.map(item => item.name) ?? [];

  const knowledgeGraphSections =
    kgExportGroups != null && kgExportGroups.length > 0
      ? [
          {
            title: t('chat:inventory.sections.knowledgeGraph'),
            textItems: [],
            htmlItems: [],
            kgExportGroups,
          },
        ]
      : [];

  const documentSearchItems =
    inventory?.['document-search']?.items?.filter(
      item => (item.documents?.length ?? 0) > 0
    ) ?? [];

  const citationLines = documentSearchItems.map(item => {
    const rawQuestion = item.question ?? '';
    const question = rawQuestion ? `"${rawQuestion}"` : '';
    const count = item.documents?.length ?? 0;
    return t('chat:inventory.citationsForQuestionFoundDocuments', {
      question,
      count,
    });
  });

  const sections: SourcesSection[] = [
    {
      title: t('chat:inventory.sections.entities', { count: entities.length }),
      textItems: entities,
      htmlItems: entities,
    },
    {
      title: t('chat:inventory.sections.timeSeries', {
        count: timeseries.length,
      }),
      textItems: timeseries,
      htmlItems: timeseries,
    },
    {
      title: t('chat:inventory.sections.financials', {
        count: financialStatementItems.length,
      }),
      textItems: financialStatementsText,
      htmlItems: financialStatementsHtml,
    },
    {
      title: t('chat:inventory.sections.watchlist', {
        count: watchlistNames.length,
      }),
      textItems: watchlistNames,
      htmlItems: watchlistNames,
    },
    ...knowledgeGraphSections,
    {
      title: t('chat:inventory.sections.citations', {
        count: citationLines.length,
      }),
      textItems: citationLines,
      htmlItems: citationLines,
    },
    {
      title: t('chat:inventory.sections.insights', {
        count: insightHeadlines.length,
      }),
      textItems: insightHeadlines,
      htmlItems: insightHeadlines,
    },
    {
      title: t('chat:inventory.sections.news', {
        count: newsHeadlines.length,
      }),
      textItems: newsHeadlines,
      htmlItems: newsHeadlines,
    },
    {
      title: t('chat:inventory.sections.externalData', {
        count: externalData.length,
      }),
      textItems: externalData,
      htmlItems: externalData
        .filter((url): url is string => url != null)
        .map(url => htmlNodes.a(url, url)),
    },
    {
      title: t('chat:inventory.sections.webLinks', { count: webLinks.length }),
      textItems: webLinks,
      htmlItems: webLinks
        .filter((url): url is string => url != null)
        .map(url => htmlNodes.a(url, url)),
    },
  ];

  const nonEmpty = sections.filter(
    s =>
      (s.kgExportGroups != null && s.kgExportGroups.length > 0) ||
      s.textItems.length > 0
  );

  if (!nonEmpty.length) {
    return { html, text };
  }

  text += `\n\n## ${t('chat:inventory.sources')}`;
  html += htmlNodes.h2(t('chat:inventory.sources'));

  nonEmpty.forEach(s => {
    if (s.kgExportGroups != null && s.kgExportGroups.length > 0) {
      text +=
        `\n\n### ${s.title}\n` + kgExportGroupsToPlainText(s.kgExportGroups);
      html += htmlNodes.h3(s.title) + kgExportGroupsToHtml(s.kgExportGroups);
      return;
    }
    text += `\n\n### ${s.title}\n` + s.textItems.map(v => `- ${v}`).join('\n');
    html += htmlNodes.h3(s.title);
    html += htmlNodes.unorderedList(
      s.htmlItems
        .filter(v => v !== null && v !== undefined)
        .map(v => htmlNodes.unorderedListItem(v))
        .join('')
    );
  });

  return { html, text };
};
